import {
  ActionIcon,
  Avatar,
  Badge,
  Box,
  CloseButton,
  Flex,
  Group,
  Menu,
  Stack,
  Text,
  ThemeIcon,
} from "@mantine/core";
import { useCallback, useEffect, useState } from "react";
import { useLanguage } from "../../../../stores/LanguageStore";
import { DatePickerInput } from "@mantine/dates";
import {
  IconArrowBarLeft,
  IconArrowBarRight,
  IconCalendarWeek,
  IconCheck,
  IconChevronDown,
  IconMailCheck,
  IconMailX,
  IconX,
} from "@tabler/icons-react";
import {
  getLastXDaysStartAndEnd,
  getMonthStartAndEnd,
  getWeekStartAndEnd,
  getYearStartAndEnd,
  parseDate,
} from "../../../../functions/dates";
import MainTable from "../../../../layouts/Table/MainTable";
import numberColumn from "../../../../layouts/Table/columns/numberColumn";
import { useGridApiRef } from "@mui/x-data-grid-pro";
import { useContactHandler } from "../../lists/components/useContactHandler";
import { useUIStore } from "../../../../stores/UIStore";
import { getAllUsers } from "../../../../api/AdminAPI";
import EngagementColumn from "./EngagementColumn";
import UsersStats from "./stats/UsersStats";
import {
  formatPhone,
  transformFilterToServer,
} from "../../../../functions/utilFunctions";
import TableFilterCopyContacts from "../../lists/components/copy/TableFilterCopyContacts";
import { findList } from "../../../../api/ContactAPI";
import ResellersUsersExport from "./ResellersUsersExport";

const contactlistId = 41774;

const ResellersUsers = () => {
  const lang = useLanguage((s) => s.language);
  const [maximizeRight, setMaximizeRight] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [dateRange, setDateRange] = useState({
    start_date: null,
    end_date: null,
  });
  const [filterModel, setFilterModel] = useState(null);
  const [loadProfile, setLoadProfile] = useState(true);
  const [listProfile, setListProfile] = useState(null);

  const handleDateChange = useCallback(
    (v) =>
      setDateRange({
        start_date: v[0],
        end_date: v[1],
      }),
    []
  );

  const apiRef = useGridApiRef();

  const columnsHandler = useCallback(() => {
    const lang = useLanguage.getState().language;
    return [
      numberColumn(),
      {
        field: "email_validated",
        headerName: "Validé",
        type: "boolean",
        width: 64,
        options: [
          {
            label: "Oui",
            value: "1",
            // defaultColor: "fab005",
          },
          {
            label: "Non",
            value: "0",
            // defaultColor: "fab005",
          },
        ],
        renderCell: ({ value }) => {
          return value ? (
            <ThemeIcon color="green.8">
              <IconMailCheck />
            </ThemeIcon>
          ) : (
            <ThemeIcon color="red.8">
              <IconMailX />
            </ThemeIcon>
          );
        },
      },
      {
        field: "last_name",
        headerName: lang.users_list.columns.last_name,
        width: 170,
        renderCell: ({ row, value }) => (
          <Group wrap="nowrap">
            <Avatar src={row.avatar} size={32} />
            {value}
          </Group>
        ),
      },
      {
        field: "first_name",
        headerName: lang.users_list.columns.first_name,
        width: 150,
      },
      {
        field: "is_subscription_active",
        headerName: "Statut",
        width: 125,
        type: "options",
        disabledCondition: true,
        valueGetter: ({ value }) => {
          return value?.active ? "active" : "not_active";
        },
        options: [
          {
            label: "Payant",
            value: "subscriber",
          },
          {
            label: "Essai",
            value: "trial",
          },
          {
            label: "Gratuit",
            value: "free",
          },
        ],
        renderCell: ({ row }) => {
          return row?.is_subscription_active === "subscriber" ? (
            <Badge
              color={"tertiary.8"}
              size="lg"
              py={12}
              variant="light"
              fz={14}
              fw={500}
              tt={"capitalize"}
            >
              {"Payant"}
            </Badge>
          ) : row?.is_subscription_active === "trial" ? (
            <Badge
              color={"orange"}
              size="lg"
              py={12}
              variant="light"
              fz={14}
              fw={500}
              tt={"capitalize"}
            >
              {"Essai"}
            </Badge>
          ) : (
            <Badge
              color={"red"}
              size="lg"
              py={12}
              variant="light"
              fz={14}
              fw={500}
              tt={"capitalize"}
            >
              {"Gratuit"}
            </Badge>
          );
        },
      },
      {
        field: "created_on",
        headerName: "Date d'inscription",
        width: 150,
        type: "date",
        valueGetter: ({ value }) => {
          return value?.length ? new Date(value) : null;
        },
        renderCell: ({ value }) => (value ? parseDate(value) : "-"),
      },
      {
        field: "number_connections",
        headerName: "Engagement",
        minWidth: 150,
        options: [
          {
            label: "Elevé ( > 60 )",
            value: "60",
            condition: "more_or_equal_than",
          },
          {
            label: "Moyen + ( > 30 )",
            value: "30",
            condition: "more_or_equal_than",
          },
          {
            label: "Moyen - ( > 10 )",
            value: "10",
            condition: "more_or_equal_than",
          },
          {
            label: "Faible ( >= 1 )",
            value: "1",
            condition: "more_or_equal_than",
          },
          {
            label: "Inactif ( 0 )",
            value: "0",
            condition: "equals",
          },
        ],
        renderCell: (props) => <EngagementColumn {...props} />,
      },
      {
        field: "source",
        headerName: "Source",
        minWidth: 150,
        type: "boolean",
        options: [
          {
            label: lang.new_ui_onboarding.web_search,
            value: "web_search",
          },
          {
            label: lang.new_ui_onboarding.linkedin_post,
            value: "linkedin_post",
          },
          {
            label: lang.new_ui_onboarding.linkedin_message,
            value: "linkedin_message",
          },
          {
            label: lang.new_ui_onboarding.referral,
            value: "referral",
          },
          { label: lang.new_ui_onboarding.email, value: "email" },
          {
            label: lang.new_ui_onboarding.trade_show,
            value: "trade_show",
          },
          { label: lang.new_ui_onboarding.other, value: "other" },
        ],
        renderCell: ({ value, row }) => {
          if (value) {
            return lang.new_ui_onboarding[value];
          } else if (row.in_a_reseller) {
            return <Text c={"red"}>Revendeur</Text>;
          }
          return "-";
        },
      },
      // tagsColumn({
      //   apiRef,
      //   editFunction:
      //     (row) =>
      //     ({ id, tags_ids }) =>
      //       updateProfile(id, { tags_ids }),
      // }),
      {
        field: "in_a_reseller",
        headerName: "User direct",
        minWidth: 50,
        type: "exist_revert",

        renderCell: ({ value }) => {
          if (!value) {
            return (
              <ThemeIcon
                radius={"xl"}
                size={"lg"}
                color="green.8"
                variant="light"
              >
                <IconCheck />
              </ThemeIcon>
            );
          }
          return (
            <ThemeIcon radius={"xl"} size={"lg"} color="red.7" variant="light">
              <IconX />
            </ThemeIcon>
          );
        },
      },
      {
        field: "number_programmations",
        headerName: "Campagnes",
        minWidth: 120,
        type: "number",
        align: "left",
        headerAlign: "left",
      },
      {
        field: "number_integrations_email",
        headerName: "Emails",
        minWidth: 100,
        type: "number",
        align: "left",
        headerAlign: "left",
      },
      {
        field: "number_integrations_linkedin",
        headerName: "LinkedIn",
        minWidth: 100,
        type: "number",
        align: "left",
        headerAlign: "left",
      },
      {
        field: "number_contact_list",
        headerName: "Listes",
        minWidth: 100,
        type: "number",
        align: "left",
        headerAlign: "left",
      },
      {
        field: "number_model",
        headerName: "Modèles",
        minWidth: 100,
        type: "number",
        align: "left",
        headerAlign: "left",
      },
      {
        field: "sum_billed",
        headerName: "C.A généré",
        width: 100,
        type: "number",
        align: "left",
        headerAlign: "left",
        renderCell: ({ value, row }) =>
          row?.is_subscription_active === "subscriber" &&
          value === 0 &&
          !row.in_a_reseller
            ? "Sellsy"
            : `${Math.floor(value)} €` || "-",
      },
      {
        field: "subscription_churn",
        headerName: "Churn",
        width: 64,
        type: "exist",
        renderCell: ({ value }) => {
          if (value) {
            return (
              <ThemeIcon
                radius={"xl"}
                size={"lg"}
                color="red.8"
                variant="subtle"
              >
                <IconCheck />
              </ThemeIcon>
            );
          }
          return (
            <ThemeIcon
              radius={"xl"}
              size={"lg"}
              color="green.7"
              variant="subtle"
            >
              <IconX />
            </ThemeIcon>
          );
        },
      },
      {
        field: "last_activity",
        headerName: "Dernière activité",
        width: 150,
        type: "date",
        valueGetter: ({ value }) => {
          return value?.length ? new Date(value) : null;
        },
        renderCell: ({ value }) => (value ? parseDate(value) : "-"),
      },
      {
        field: "email",
        headerName: lang.users_list.columns.email,
        minWidth: 200,
        valueGetter: ({ value }) => value || "-",
      },
      {
        field: "mobile_phone",
        headerName: lang.profile.mobile_phone,
        minWidth: 200,
        valueGetter: ({ value }) => {
          return formatPhone(value);
        },
      },
      {
        field: "company",
        headerName: lang.profile.company,
        minWidth: 200,
        valueGetter: ({ value }) => value || "-",
      },
      {
        field: "job_title",
        headerName: lang.profile.job_title,
        minWidth: 200,
        valueGetter: ({ value }) => value || "-",
      },
    ];
  }, [apiRef]);

  const {
    loading,
    columns,
    rows,
    rowCount,
    handleLoading,
    onFilterChange,
    onPaginationChange,
    onSortChange,
  } = useContactHandler({
    apiRef,
    getFirstPage: ({ per_page, filter, sort, query, page }) =>
      getAllUsers({
        per_page,
        filter,
        sort,
        query,
        date_range: dateRange.end_date ? dateRange : null,
        page,
      }),
    searchFunction: getAllUsers,
    columnsHandler,
    disableQueryTransform: true,
    resultsHandler: (results) => results,
    isMagileads: true,
    additionalMainRequest: () => setInitialized(true),
  });

  const toolbarActions = useCallback(
    ({ selectedIds, filter }) => (
      <ResellersUsersExport
        id={contactlistId}
        selectedIds={selectedIds}
        filter={filter}
        filterResultNumber={rowCount}
        total={listProfile?.number_of_contacts}
        onFinish={() => setLoadProfile(true)}
      />
    ),
    [listProfile, rowCount, setLoadProfile]
  );

  useEffect(() => {
    if (dateRange.end_date && dateRange.start_date) {
      handleLoading(true);
    }
    if (dateRange?.needRefresh) {
      handleLoading(true);
    }
  }, [dateRange.end_date, dateRange.start_date]);

  useEffect(() => {
    useUIStore.getState().setHeaderClosed(true);

    return () => {
      useUIStore.getState().setHeaderClosed(false);
    };
  }, []);

  useEffect(() => {
    if (loadProfile) {
      findList(contactlistId)
        .then((r) => {
          setListProfile(r.data.contact_list_profile);
        })
        .finally(() => setLoadProfile(false));
    }
  }, [loadProfile]);

  if (!columns) {
    return;
  }

  return (
    <Group gap={"xl"} pt={16} px={32} wrap="nowrap" align="stretch">
      {!maximizeRight && (
        <Stack w={"22%"}>
          <DatePickerInput
            type="range"
            placeholder={lang.reports.period}
            leftSection={<IconCalendarWeek size={18} />}
            value={[dateRange.start_date, dateRange.end_date]}
            maxDate={new Date()}
            onChange={handleDateChange}
            w={360}
            radius={"md"}
            styles={{
              input: {
                borderColor: "var(--mantine-color-primary-0)",
              },
            }}
            valueFormat="DD / MM / YYYY"
            size="md"
            rightSectionPointerEvents="all"
            rightSectionWidth={90}
            rightSection={
              <Group justify="end" w={"100%"} pr={8}>
                {dateRange.start_date && (
                  <CloseButton
                    onClick={() => {
                      setDateRange({
                        start_date: null,
                        end_date: null,
                        needRefresh: true,
                      });
                    }}
                  />
                )}
                <Menu>
                  <Menu.Target>
                    <ActionIcon variant="light">
                      <IconChevronDown />
                    </ActionIcon>
                  </Menu.Target>
                  <Menu.Dropdown>
                    <Menu.Item
                      onClick={() => {
                        let result = getLastXDaysStartAndEnd(30);
                        setDateRange({
                          start_date: new Date(result.start_date),
                          end_date: new Date(result.end_date),
                        });
                      }}
                      variant="subtle"
                      size="md"
                    >
                      30 derniers jours
                    </Menu.Item>

                    <Menu.Item
                      onClick={() => {
                        let thisWeek = getWeekStartAndEnd(new Date());
                        setDateRange({
                          start_date: thisWeek.weekStart,
                          end_date: thisWeek.weekEnd,
                        });
                      }}
                      variant="subtle"
                      size="md"
                    >
                      {lang.reporting.period_week}
                    </Menu.Item>

                    <Menu.Item
                      onClick={() => {
                        let thisMonth = getMonthStartAndEnd(new Date());
                        setDateRange({
                          start_date: thisMonth.monthStart,
                          end_date: thisMonth.monthEnd,
                        });
                      }}
                      variant="subtle"
                      size="md"
                    >
                      {lang.reporting.period_month}
                    </Menu.Item>

                    <Menu.Item
                      onClick={() => {
                        let thisYear = getYearStartAndEnd(new Date());
                        setDateRange({
                          start_date: thisYear.yearStart,
                          end_date: thisYear.yearEnd,
                        });
                      }}
                      variant="subtle"
                      size="md"
                    >
                      Cette année
                    </Menu.Item>
                  </Menu.Dropdown>
                </Menu>
              </Group>
            }
          />

          <UsersStats date_range={dateRange} filter={filterModel} />
        </Stack>
      )}

      <Box w={2} bg={"primary.3"}>
        <Stack pos={"relative"} h={"100%"} align="center" justify="center">
          <ActionIcon.Group wrap="nowrap" gap={0}>
            {maximizeRight ? (
              <ActionIcon
                onClick={() => setMaximizeRight(false)}
                color="primary.6"
                size={"sm"}
              >
                <IconArrowBarRight />
              </ActionIcon>
            ) : (
              <ActionIcon
                onClick={() => setMaximizeRight(true)}
                color="primary.6"
                size={"sm"}
              >
                <IconArrowBarLeft />
              </ActionIcon>
            )}
          </ActionIcon.Group>
        </Stack>
      </Box>

      <Flex h={"calc(100vh - 32px)"} flex={"1 1 auto"}>
        <MainTable
          isClient={false}
          apiRef={apiRef}
          columns={columns}
          rows={rows}
          rowCount={rowCount}
          loading={loading}
          setLoading={() => {
            handleLoading();
          }}
          id={"list-table"}
          onPaginationChange={onPaginationChange}
          onFilterChange={(model) => {
            onFilterChange();
            setFilterModel(transformFilterToServer(model));
          }}
          onSortChange={onSortChange}
          actionsCellWidth={50}
          withBorderTop
          whiteButtons
          defaultSort={[{ field: "id", sort: "desc" }]}
          toolbarActions={toolbarActions}
        />
      </Flex>
    </Group>
  );
};

export default ResellersUsers;
